import type { PropType } from '@prisma/client';
import { manualStatPhrase, teamScopedStatCategory, combinesBothTeams } from './manual-stat-options';

export interface ManualPropTemplateInput {
  propType: PropType;
  subjectTeam?: string | null;
  subjectPlayer?: string | null;
  threshold?: number | null;
  statCategory?: string | null;
  customDescription?: string | null;
  /** For MATCH_THRESHOLD — provided by the route/fan-out at substitution time. */
  homeTeam?: string | null;
  awayTeam?: string | null;
}

export interface ManualPropValidationError {
  field: string;
  message: string;
}

/**
 * How a stat category reads inside the question.
 *
 * Curated categories carry their own phrasing, because de-underscoring the id
 * produces wording that is at best clumsy and at worst misleading —
 * TEAM_LONGEST_FIELD_GOAL would read "3+ team longest field goal". Anything
 * not in the catalog keeps the original de-underscored form, so hand-typed
 * and legacy categories are untouched.
 */
const formatStat = (stat: string | null | undefined): string =>
  manualStatPhrase(stat) ?? (stat ?? '').toLowerCase().replace(/_/g, ' ').trim();

/**
 * The match-total question. Shared so the two routes into it — an explicit
 * COMBINED_TOTAL scope, and a summed category under any other scope — cannot
 * word the same prop two different ways.
 */
const matchTotalQuestion = (
  threshold: number | null | undefined,
  stat: string,
  home: string,
  away: string
): string => `Will ${threshold}+ ${stat} be recorded in ${home} vs ${away}?`;

const trimOrNull = (s: string | null | undefined): string | null => {
  if (s == null) return null;
  const t = s.trim();
  return t.length === 0 ? null : t;
};

/**
 * Build a human-readable description from structured manual-prediction fields.
 *
 * Examples:
 *   TEAM_THRESHOLD  + Argentina + 2 + GOALS                 → "Will Argentina record 2+ goals?"
 *   MATCH_THRESHOLD + 3 + GOALS + home=Argentina/away=Brazil → "Will Argentina or Brazil record 3+ goals?"
 *   MATCH_THRESHOLD + 3 + GOALS + no teams                  → "Will <Home> or <Away> record 3+ goals?" (preview only)
 *   PLAYER_BOOLEAN  + Lionel Messi + 1 + GOALS              → "Will Lionel Messi record 1+ goals?"
 *   PLAYER_BOOLEAN  + Lionel Messi + null + GOALS           → "Will Lionel Messi record any goals?"
 *   GENERIC         + customDescription                     → customDescription verbatim
 *
 * customDescription is honored as an override on TEAM/MATCH/PLAYER templates.
 */
export function buildManualPropDescription(input: ManualPropTemplateInput): string {
  if (input.customDescription && input.customDescription.trim().length > 0) {
    return input.customDescription.trim();
  }

  switch (input.propType) {
    case 'MANUAL_TEAM_THRESHOLD': {
      const team = trimOrNull(input.subjectTeam);
      const stat = formatStat(input.statCategory);
      if (!team || input.threshold == null || !stat) return '';
      return `Will ${team} record ${input.threshold}+ ${stat}?`;
    }
    case 'MANUAL_MATCH_THRESHOLD': {
      const stat = formatStat(input.statCategory);
      if (input.threshold == null || !stat) return '';
      const home = trimOrNull(input.homeTeam) ?? '<Home>';
      const away = trimOrNull(input.awayTeam) ?? '<Away>';
      // The CATEGORY decides the wording, not the scope the admin picked. A
      // category that adds both teams settles as a match total, so it has to
      // read as one — "X or Y record N+" would describe a harder question
      // than the one being sold.
      if (combinesBothTeams(input.statCategory)) {
        return matchTotalQuestion(input.threshold, stat, home, away);
      }
      return `Will ${home} or ${away} record ${input.threshold}+ ${stat}?`;
    }
    case 'MANUAL_PLAYER_BOOLEAN': {
      const player = trimOrNull(input.subjectPlayer);
      const stat = formatStat(input.statCategory);
      if (!player || !stat) return '';
      if (input.threshold != null) {
        return `Will ${player} record ${input.threshold}+ ${stat}?`;
      }
      return `Will ${player} record any ${stat}?`;
    }
    case 'MANUAL_GENERIC':
      return input.customDescription?.trim() ?? '';
    default:
      return '';
  }
}

/**
 * Validate manual-prop template input. Returns an array of errors (empty if valid).
 */
export function validateManualPropInput(
  input: ManualPropTemplateInput,
  /**
   * Set when the caller is fanning the template out across the round. A
   * TEAM_THRESHOLD fan-out derives a team per game, so demanding one up front
   * is what made "apply to every team" impossible to express.
   */
  options: { fanOut?: boolean } = {}
): ManualPropValidationError[] {
  const errors: ManualPropValidationError[] = [];

  switch (input.propType) {
    case 'MANUAL_TEAM_THRESHOLD':
      if (!options.fanOut && !input.subjectTeam?.trim()) {
        errors.push({ field: 'subjectTeam', message: 'Team is required' });
      }
      if (input.threshold == null || Number.isNaN(input.threshold)) {
        errors.push({ field: 'threshold', message: 'Threshold is required' });
      }
      if (!input.statCategory?.trim()) errors.push({ field: 'statCategory', message: 'Stat category is required' });
      if (input.subjectPlayer?.trim()) {
        errors.push({ field: 'subjectPlayer', message: 'Not allowed for TEAM_THRESHOLD' });
      }
      break;
    case 'MANUAL_MATCH_THRESHOLD':
      if (input.threshold == null || Number.isNaN(input.threshold)) {
        errors.push({ field: 'threshold', message: 'Threshold is required' });
      }
      if (!input.statCategory?.trim()) errors.push({ field: 'statCategory', message: 'Stat category is required' });
      if (input.subjectTeam?.trim()) {
        errors.push({ field: 'subjectTeam', message: 'Not allowed for MATCH_THRESHOLD (use applyToAllGames or single gameId)' });
      }
      if (input.subjectPlayer?.trim()) {
        errors.push({ field: 'subjectPlayer', message: 'Not allowed for MATCH_THRESHOLD' });
      }
      break;
    case 'MANUAL_PLAYER_BOOLEAN':
      if (!input.subjectPlayer?.trim()) errors.push({ field: 'subjectPlayer', message: 'Player is required' });
      if (!input.statCategory?.trim()) errors.push({ field: 'statCategory', message: 'Stat category is required' });
      if (input.subjectTeam?.trim()) {
        errors.push({ field: 'subjectTeam', message: 'Not allowed for PLAYER_BOOLEAN' });
      }
      break;
    case 'MANUAL_GENERIC':
      if (!input.customDescription?.trim()) {
        errors.push({ field: 'customDescription', message: 'Description is required' });
      }
      if (input.subjectTeam?.trim()) {
        errors.push({ field: 'subjectTeam', message: 'Not allowed for GENERIC' });
      }
      if (input.subjectPlayer?.trim()) {
        errors.push({ field: 'subjectPlayer', message: 'Not allowed for GENERIC' });
      }
      if (input.threshold != null) {
        errors.push({ field: 'threshold', message: 'Not allowed for GENERIC' });
      }
      if (input.statCategory?.trim()) {
        errors.push({ field: 'statCategory', message: 'Not allowed for GENERIC' });
      }
      break;
    case 'ODDS_BASED':
      errors.push({ field: 'propType', message: 'ODDS_BASED props use auto-populate, not manual templates' });
      break;
  }

  return errors;
}

// ============================================================================
// FAN-OUT
// ============================================================================

export interface FanOutGame {
  id: string;
  homeTeam: string;
  awayTeam: string;
}

export interface FanOutDraft {
  gameId: string;
  description: string;
  /**
   * Per-draft propType override. For MATCH_THRESHOLD with EACH_TEAM scope, drafts
   * are stored as TEAM_THRESHOLD with subjectTeam set so they read like a single-team
   * prediction. For other scopes, this matches the template's propType.
   */
  propType: PropType;
  subjectTeam: string | null;
  /**
   * Per-draft stat override. EACH_TEAM drafts settle against ONE team's box
   * score, so a game-wide category has to become its single-team form — left
   * as-is, both sides of a game would resolve to the same combined total while
   * reading as two different questions. Null means "use the template's".
   */
  statCategory: string | null;
}

/**
 * For MATCH_THRESHOLD fan-out, the admin chooses how the threshold applies per game:
 *   - EITHER_TEAM     (default): one prop per game — "X or Y record N+ stat?"
 *   - EACH_TEAM       : two props per game (one per team) — "X record N+ stat against Y?"
 *   - COMBINED_TOTAL  : one prop per game — "N+ stat recorded in this match?"
 * Not stored on the prop row; transient creation-time abstraction.
 */
export type MatchScope = 'EITHER_TEAM' | 'EACH_TEAM' | 'COMBINED_TOTAL';

/**
 * Whether a propType supports fan-out across multiple games.
 *
 * MATCH_THRESHOLD (scoped per the admin's choice), GENERIC (free text
 * duplicated) and TEAM_THRESHOLD (one prop per team, per game) all fan out.
 * PLAYER_BOOLEAN names one player, so it is inherently single-game.
 *
 * TEAM_THRESHOLD used to be excluded, which left "ask this of every team in
 * the round" reachable ONLY by picking the Match Threshold template and then
 * the Each-team scope — a path nobody finds from the Team Threshold template
 * they already chose. Leaving the team blank there just failed validation.
 */
export function supportsFanOut(propType: PropType): boolean {
  return (
    propType === 'MANUAL_MATCH_THRESHOLD' ||
    propType === 'MANUAL_GENERIC' ||
    propType === 'MANUAL_TEAM_THRESHOLD'
  );
}

/**
 * Expand a manual-prop template across multiple games into N (or 2N) drafts.
 * For MATCH_THRESHOLD with EACH_TEAM scope, emits 2 drafts per game (one per side).
 * Returns empty array if the propType doesn't support fan-out or games is empty.
 */
export function expandManualPropAcrossGames(
  template: ManualPropTemplateInput,
  games: FanOutGame[],
  matchScope: MatchScope = 'EITHER_TEAM'
): FanOutDraft[] {
  if (!supportsFanOut(template.propType) || games.length === 0) return [];

  // GENERIC always uses the admin's customDescription verbatim — no scoping.
  if (template.propType === 'MANUAL_GENERIC') {
    return games.map((g) => ({
      gameId: g.id,
      description: buildManualPropDescription(template),
      propType: 'MANUAL_GENERIC' as PropType,
      subjectTeam: null,
      statCategory: null,
    }));
  }

  const threshold = template.threshold;

  // TEAM_THRESHOLD fans out to every team in the round — the same shape
  // EACH_TEAM produces, deliberately routed through the identical branch so
  // the two ways of asking for it cannot drift into different props.
  const scope: MatchScope =
    template.propType === 'MANUAL_TEAM_THRESHOLD' ? 'EACH_TEAM' : matchScope;

  if (scope === 'EACH_TEAM') {
    // These read as one team's question and settle as one team's question.
    const teamStat = teamScopedStatCategory(template.statCategory);
    const stat = formatStat(teamStat);
    const drafts: FanOutDraft[] = [];
    for (const g of games) {
      drafts.push({
        gameId: g.id,
        description: `Will ${g.homeTeam} record ${threshold}+ ${stat} against ${g.awayTeam}?`,
        propType: 'MANUAL_TEAM_THRESHOLD',
        subjectTeam: g.homeTeam,
        statCategory: teamStat,
      });
      drafts.push({
        gameId: g.id,
        description: `Will ${g.awayTeam} record ${threshold}+ ${stat} against ${g.homeTeam}?`,
        propType: 'MANUAL_TEAM_THRESHOLD',
        subjectTeam: g.awayTeam,
        statCategory: teamStat,
      });
    }
    return drafts;
  }

  const stat = formatStat(template.statCategory);

  if (scope === 'COMBINED_TOTAL') {
    return games.map((g) => ({
      gameId: g.id,
      description: matchTotalQuestion(threshold, stat, g.homeTeam, g.awayTeam),
      propType: 'MANUAL_MATCH_THRESHOLD',
      subjectTeam: null,
      statCategory: null,
    }));
  }

  // EITHER_TEAM (default) — existing behavior. buildManualPropDescription
  // still re-words a summed category as a match total, so an either-team
  // scope can never mis-describe a stat that adds both sides.
  return games.map((g) => ({
    gameId: g.id,
    description: buildManualPropDescription({
      ...template,
      homeTeam: g.homeTeam,
      awayTeam: g.awayTeam,
    }),
    propType: 'MANUAL_MATCH_THRESHOLD',
    subjectTeam: null,
    statCategory: null,
  }));
}

// ============================================================================
// CSV PARSING
// ============================================================================

export interface ManualPropCsvRow {
  rowNumber: number; // 1-indexed, NOT counting header (row 1 = first data row)
  propType: PropType;
  pickMode: 'BUY_ONLY' | 'YES_NO';
  costCoins: number;
  bonusMultiplier: number;
  subjectTeam: string | null;
  subjectPlayer: string | null;
  threshold: number | null;
  statCategory: string | null;
  customDescription: string | null;
  gameId: string | null;
  applyToAllGames: boolean;
  matchScope: MatchScope | null;
}

export interface CsvRowError {
  rowNumber: number;
  field: string;
  message: string;
}

export interface ParseManualPropCsvResult {
  rows: ManualPropCsvRow[];
  errors: CsvRowError[];
}

const REQUIRED_COLUMNS = [
  'propType',
  'pickMode',
  'costCoins',
  'bonusMultiplier',
  'subjectTeam',
  'subjectPlayer',
  'threshold',
  'statCategory',
  'customDescription',
  'gameId',
  'applyToAllGames',
] as const;

// matchScope is OPTIONAL — older CSVs without this column still parse fine.
const OPTIONAL_COLUMNS = ['matchScope'] as const;
const VALID_MATCH_SCOPES: MatchScope[] = ['EITHER_TEAM', 'EACH_TEAM', 'COMBINED_TOTAL'];

const VALID_PROP_TYPES: PropType[] = [
  'MANUAL_TEAM_THRESHOLD',
  'MANUAL_MATCH_THRESHOLD',
  'MANUAL_PLAYER_BOOLEAN',
  'MANUAL_GENERIC',
];

const VALID_PICK_MODES = ['BUY_ONLY', 'YES_NO'] as const;
type CsvPickMode = (typeof VALID_PICK_MODES)[number];

/**
 * Minimal CSV parser. Supports double-quoted fields (with embedded commas and `""` escape).
 * Does not support multi-line quoted fields — each row must be one physical line.
 */
function parseCsvLine(line: string): string[] {
  const out: string[] = [];
  let cur = '';
  let inQuotes = false;
  let i = 0;
  while (i < line.length) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"') {
        if (line[i + 1] === '"') {
          cur += '"';
          i += 2;
          continue;
        }
        inQuotes = false;
        i++;
        continue;
      }
      cur += ch;
      i++;
      continue;
    }
    if (ch === '"') {
      inQuotes = true;
      i++;
      continue;
    }
    if (ch === ',') {
      out.push(cur);
      cur = '';
      i++;
      continue;
    }
    cur += ch;
    i++;
  }
  out.push(cur);
  return out;
}

const parseCsvBoolean = (raw: string): boolean | null => {
  const v = raw.trim().toLowerCase();
  if (v === '' || v === 'false' || v === '0' || v === 'no') return false;
  if (v === 'true' || v === '1' || v === 'yes') return true;
  return null;
};

/**
 * Parse manual-prop CSV text into typed rows + per-row errors.
 * Header row required; column order is fixed per REQUIRED_COLUMNS.
 * If header is malformed, returns a single error with rowNumber=0.
 */
export function parseManualPropCsv(text: string): ParseManualPropCsvResult {
  const errors: CsvRowError[] = [];
  const rows: ManualPropCsvRow[] = [];

  // Strip optional UTF-8 BOM (Excel-saved CSVs include this); split on any CR/LF combo.
  const cleaned = text.replace(/^﻿/, '');
  const lines = cleaned
    .split(/\r\n|\r|\n/)
    .map((l) => l.trim())
    .filter((l) => l.length > 0);

  if (lines.length === 0) {
    errors.push({ rowNumber: 0, field: 'file', message: 'CSV is empty' });
    return { rows, errors };
  }

  // Reject lines with unbalanced quotes (multi-line quoted fields not supported)
  for (let i = 0; i < lines.length; i++) {
    let quoteCount = 0;
    for (const ch of lines[i]) if (ch === '"') quoteCount++;
    if (quoteCount % 2 !== 0) {
      errors.push({
        rowNumber: i,
        field: i === 0 ? 'header' : 'row',
        message: 'Unbalanced quote — multi-line quoted fields are not supported',
      });
      return { rows, errors };
    }
  }

  const header = parseCsvLine(lines[0]).map((h) => h.trim());
  const missingColumns = REQUIRED_COLUMNS.filter((c) => !header.includes(c));
  if (missingColumns.length > 0) {
    errors.push({
      rowNumber: 0,
      field: 'header',
      message: `Missing required columns: ${missingColumns.join(', ')}`,
    });
    return { rows, errors };
  }

  const colIndex: Record<string, number> = {};
  header.forEach((h, idx) => {
    colIndex[h] = idx;
  });

  for (let i = 1; i < lines.length; i++) {
    const rowNumber = i; // 1-indexed data row
    const cells = parseCsvLine(lines[i]);
    const get = (col: string): string => (cells[colIndex[col]] ?? '').trim();

    const propTypeRaw = get('propType');
    const pickModeRaw = get('pickMode');
    const costRaw = get('costCoins');
    const multRaw = get('bonusMultiplier');
    const thresholdRaw = get('threshold');
    const fanOutRaw = get('applyToAllGames');

    let rowHadError = false;

    if (!VALID_PROP_TYPES.includes(propTypeRaw as PropType)) {
      errors.push({
        rowNumber,
        field: 'propType',
        message: `Must be one of ${VALID_PROP_TYPES.join(', ')}`,
      });
      rowHadError = true;
    }

    if (!(VALID_PICK_MODES as readonly string[]).includes(pickModeRaw)) {
      errors.push({
        rowNumber,
        field: 'pickMode',
        message: `Must be one of ${VALID_PICK_MODES.join(', ')}`,
      });
      rowHadError = true;
    }

    const costCoins = Number(costRaw);
    if (!Number.isFinite(costCoins) || costCoins < 1 || costCoins > 1_000_000) {
      errors.push({
        rowNumber,
        field: 'costCoins',
        message: 'Must be a positive finite number between 1 and 1,000,000',
      });
      rowHadError = true;
    }

    const bonusMultiplier = Number(multRaw);
    if (!Number.isFinite(bonusMultiplier) || bonusMultiplier < 0.01 || bonusMultiplier > 100) {
      errors.push({
        rowNumber,
        field: 'bonusMultiplier',
        message: 'Must be a finite number between 0.01 and 100',
      });
      rowHadError = true;
    }

    let threshold: number | null = null;
    if (thresholdRaw !== '') {
      const t = Number(thresholdRaw);
      if (!Number.isFinite(t) || Math.abs(t) > 10_000) {
        errors.push({ rowNumber, field: 'threshold', message: 'Must be a finite number with |value| ≤ 10,000 when provided' });
        rowHadError = true;
      } else {
        threshold = t;
      }
    }

    const applyToAllGames = parseCsvBoolean(fanOutRaw);
    if (applyToAllGames === null) {
      errors.push({
        rowNumber,
        field: 'applyToAllGames',
        message: 'Must be true/false (or 1/0, yes/no, or blank)',
      });
      rowHadError = true;
    }

    // matchScope is OPTIONAL — defaults to EITHER_TEAM if absent or blank.
    const matchScopeRaw = colIndex.matchScope !== undefined ? get('matchScope') : '';
    let matchScope: MatchScope | null = null;
    if (matchScopeRaw !== '') {
      if (!VALID_MATCH_SCOPES.includes(matchScopeRaw as MatchScope)) {
        errors.push({
          rowNumber,
          field: 'matchScope',
          message: `Must be one of ${VALID_MATCH_SCOPES.join(', ')} (or blank)`,
        });
        rowHadError = true;
      } else {
        matchScope = matchScopeRaw as MatchScope;
      }
    }

    if (rowHadError) continue;

    const subjectTeam = trimOrNull(get('subjectTeam'));
    const subjectPlayer = trimOrNull(get('subjectPlayer'));
    const statCategory = trimOrNull(get('statCategory'));
    const customDescription = trimOrNull(get('customDescription'));
    const gameId = trimOrNull(get('gameId'));

    // Delegate template-specific structural validation
    const tplErrors = validateManualPropInput(
      {
        propType: propTypeRaw as PropType,
        subjectTeam,
        subjectPlayer,
        threshold,
        statCategory,
        customDescription,
      },
      // A fanned-out TEAM_THRESHOLD row derives its team per game, so the
      // column is legitimately blank.
      { fanOut: !!applyToAllGames }
    );
    if (tplErrors.length > 0) {
      tplErrors.forEach((e) => errors.push({ rowNumber, field: e.field, message: e.message }));
      continue;
    }

    // Cross-field constraints
    if (applyToAllGames && !supportsFanOut(propTypeRaw as PropType)) {
      errors.push({
        rowNumber,
        field: 'applyToAllGames',
        message: 'Fan-out is not supported for this template',
      });
      continue;
    }
    // A TEAM_THRESHOLD fan-out takes its team from each game, so naming one
    // as well is a contradiction. Silently ignoring the named team would
    // produce a round's worth of props the author did not ask for.
    if (applyToAllGames && propTypeRaw === 'MANUAL_TEAM_THRESHOLD' && subjectTeam) {
      errors.push({
        rowNumber,
        field: 'subjectTeam',
        message: 'Leave subjectTeam empty when applyToAllGames is true — a team is derived per game',
      });
      continue;
    }
    if (applyToAllGames && gameId) {
      errors.push({
        rowNumber,
        field: 'gameId',
        message: 'gameId must be empty when applyToAllGames is true',
      });
      continue;
    }

    rows.push({
      rowNumber,
      propType: propTypeRaw as PropType,
      pickMode: pickModeRaw as CsvPickMode,
      costCoins,
      bonusMultiplier,
      subjectTeam,
      subjectPlayer,
      threshold,
      statCategory,
      customDescription,
      gameId,
      applyToAllGames: !!applyToAllGames,
      matchScope,
    });
  }

  return { rows, errors };
}

/**
 * Header row + a couple of example rows for the "Download template" feature.
 * Prefixed with a UTF-8 BOM so Excel correctly detects encoding for non-ASCII names.
 */
export function manualPropCsvTemplate(): string {
  const header = REQUIRED_COLUMNS.join(',');
  const examples = [
    'MANUAL_TEAM_THRESHOLD,YES_NO,100,1.5,Argentina,,2,GOALS,,,false',
    'MANUAL_MATCH_THRESHOLD,YES_NO,100,1.5,,,3,GOALS,,,true',
    'MANUAL_PLAYER_BOOLEAN,YES_NO,150,2,,Lionel Messi,1,GOALS,,,false',
    'MANUAL_GENERIC,YES_NO,75,1,,,,,Will the match go to penalties?,,false',
  ];
  return '﻿' + [header, ...examples].join('\n') + '\n';
}
